/**
 * Global Reauthentication gate.
 *
 * Mounted ONCE inside <Layout> so it can observe route changes via
 * react-router. On every route change, it asks:
 *
 *   1. Does the current user's role have reauth enabled?
 *   2. Is the page they just navigated to flagged in
 *      `policy.reauth.page_toggles`?
 *   3. Has the user reauthenticated recently — within
 *      `policy.reauth.valid_duration_seconds` of NOW (read from
 *      sessionStorage `orb_reauth_at` / `orb_reauth_ttl`)?
 *
 * If (1) AND (2) AND NOT (3) → show <ReauthModal>. The modal blocks
 * the underlying page (Radix dialog behavior). On success the gate
 * dismisses, the user sees the protected page.
 *
 * On Cancel → bounce back to the previous route (history.back()) so
 * the user lands somewhere they're allowed to be.
 *
 * Special-user mapping: the spec says special users see the admin
 * page set, and the admin role's reauth toggles apply to them too.
 * We resolve `policyRole = 'special'` (own policy) for special users
 * but ALSO check the `admin` policy's page_toggles as a fallback —
 * so toggling admin/users to require reauth automatically requires
 * it for special users too.
 *
 * Pages with their own dedicated reauth flows (some admin pages
 * already prompt for password before specific actions like rotating
 * keys) MUST NOT be wired into the page registry — leave those out
 * of `pageRegistry.js` if their existing in-page reauth is the
 * canonical UX. Per spec: "SOME ADMIN SECTIONS ALREADY REQUIRE
 * REAUTHENTICATION TO ACCESS SPECIFIC FEATURES ON A PAGE. LEAVE
 * THOSE AS IS."
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ReauthModal, { REAUTH_AT_KEY, REAUTH_TTL_KEY } from './ReauthModal';
import { findPageIdByPath, USER_PAGES, ADMIN_PAGES } from '../constants/pageRegistry';

const getReauthValidNow = () => {
  try {
    const at = sessionStorage.getItem(REAUTH_AT_KEY);
    const ttlStr = sessionStorage.getItem(REAUTH_TTL_KEY);
    if (!at || !ttlStr) return false;
    const issued = new Date(at).getTime();
    const ttl = parseInt(ttlStr, 10) * 1000;
    if (Number.isNaN(issued) || Number.isNaN(ttl)) return false;
    return Date.now() - issued < ttl;
  } catch (_) { return false; }
};

// Cache the policy + admin policy for one minute to avoid hammering the
// backend on every route change. The cache is invalidated on logout
// (sessionStorage is cleared with the auth state).
const POLICY_CACHE_KEY = 'orb_reauth_policy_cache';
const POLICY_CACHE_TTL_MS = 60 * 1000;

const readPolicyCache = () => {
  try {
    const raw = sessionStorage.getItem(POLICY_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetched_at) return null;
    if (Date.now() - parsed.fetched_at > POLICY_CACHE_TTL_MS) return null;
    return parsed;
  } catch (_) { return null; }
};

const writePolicyCache = (data) => {
  try {
    sessionStorage.setItem(POLICY_CACHE_KEY, JSON.stringify({ ...data, fetched_at: Date.now() }));
  } catch (_) { /* ignore */ }
};

const ReauthGate = () => {
  const { user, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null); // { reauth: {...} }
  const [adminPolicy, setAdminPolicy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [pageLabel, setPageLabel] = useState(null);

  const isSpecial = !!user?.created_by_admin || !!(user?.feature_toggles || {}).admin_user_administration;
  const userRole = (user?.role || '').toLowerCase();
  const policyRole = isSpecial ? 'special' : userRole;

  // Fetch the user's role policy. The backend response includes an
  // `admin_reauth` block when the caller is a special user (see
  // /api/me/security-policy) so we never need to hit /admin/* (which
  // 403s for non-admins) just to render the gate.
  const loadPolicies = useCallback(async () => {
    if (!user) return;
    const cached = readPolicyCache();
    if (cached && cached.user_id === user.id) {
      setPolicy(cached.policy || null);
      setAdminPolicy(cached.adminPolicy || null);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/me/security-policy');
      const p = res.data || null;
      // For special users the server inlines admin_reauth so we
      // synthesize a minimal "admin policy" with just that block —
      // the gate only needs `enabled` + `page_toggles` to compute
      // requiredPageIds.
      let adminP = null;
      if (isSpecial && p?.admin_reauth) {
        adminP = { reauth: p.admin_reauth };
      }
      setPolicy(p);
      setAdminPolicy(adminP);
      writePolicyCache({ user_id: user.id, policy: p, adminPolicy: adminP });
    } catch (_) {
      setPolicy(null);
      setAdminPolicy(null);
    } finally { setLoading(false); }
  }, [user, api, isSpecial]);

  useEffect(() => { loadPolicies(); }, [loadPolicies]);

  // iter148 — Watch for admin policy mutations from
  // SecurityRequirementsPage. The page dispatches
  // 'orb-security-policy-changed' after a successful save AND clears
  // the cache key. Reload immediately so the very next protected
  // navigation picks up the new toggles instead of waiting for the
  // 60s cache TTL.
  useEffect(() => {
    const refetch = () => {
      try { sessionStorage.removeItem(POLICY_CACHE_KEY); } catch (_) { /* ignore */ }
      loadPolicies();
    };
    window.addEventListener('orb-security-policy-changed', refetch);
    return () => window.removeEventListener('orb-security-policy-changed', refetch);
  }, [loadPolicies]);

  // Build the lookup of which page-ids require reauth for this user.
  const requiredPageIds = useMemo(() => {
    const out = new Set();
    const reauth = (policy || {}).reauth || {};
    if (reauth.enabled) {
      Object.entries(reauth.page_toggles || {}).forEach(([pid, on]) => {
        if (on) out.add(pid);
      });
    }
    // Special users inherit any admin page toggled on (per spec).
    if (isSpecial && adminPolicy) {
      const ar = (adminPolicy || {}).reauth || {};
      if (ar.enabled) {
        Object.entries(ar.page_toggles || {}).forEach(([pid, on]) => {
          if (on) out.add(pid);
        });
      }
    }
    return out;
  }, [policy, adminPolicy, isSpecial]);

  // On every route change, decide whether to gate this page.
  useEffect(() => {
    if (!user || loading) return;
    const pid = findPageIdByPath(location.pathname);
    if (!pid) { setOpen(false); return; }
    if (!requiredPageIds.has(pid)) { setOpen(false); return; }
    if (getReauthValidNow()) { setOpen(false); return; }
    // Mark which page label to show in the modal.
    const all = [...USER_PAGES, ...ADMIN_PAGES];
    const match = all.find((p) => p.id === pid);
    setPageLabel(match?.label || null);
    setOpen(true);
  }, [location.pathname, user, loading, requiredPageIds]);

  if (!user) return null;

  return (
    <ReauthModal
      open={open}
      pageLabel={pageLabel}
      onSuccess={() => setOpen(false)}
      onCancel={() => {
        // User declined → bounce back. If history is empty, push to /
        // so we never strand them on a gated page with the modal closed.
        setOpen(false);
        if (window.history.length > 1) {
          navigate(-1);
        } else {
          navigate('/');
        }
      }}
    />
  );
};

export default ReauthGate;
